import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { useEffect, useMemo, useState } from 'react';
import { BidNote } from '../types';
import styles from './NotesViewModal.module.scss';

interface NotesViewModalProps {
  visible: boolean;
  onHide: () => void;
  notes: BidNote[];
  moleculeName: string;
}

const NotesViewModal: React.FC<NotesViewModalProps> = ({
  visible,
  onHide,
  notes,
  moleculeName,
}) => {
  // Group notes by section
  const groupedNotes = useMemo(() => {
    return notes.reduce(
      (acc, note) => {
        const sectionKey = note.section_name;
        if (!acc[sectionKey]) {
          acc[sectionKey] = [];
        }
        acc[sectionKey].push(note);
        return acc;
      },
      {} as Record<string, BidNote[]>
    );
  }, [notes]);

  // Sort sections alphabetically
  const sortedSections = useMemo(() => {
    return Object.keys(groupedNotes).sort();
  }, [groupedNotes]);

  // Initialize all sections as open (all indices in array)
  const [activeIndex, setActiveIndex] = useState<number | number[]>(() => {
    return sortedSections.map((_, index) => index);
  });

  // Reset to all open when modal opens or sections change
  useEffect(() => {
    if (visible && sortedSections.length > 0) {
      setActiveIndex(sortedSections.map((_, index) => index));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, sortedSections.length]);

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      // Always format as: MM-DD-YYYY HH:MM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}-${day}-${year} ${hours}:${minutes}`;
    } catch (_error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const formatSectionName = (sectionName: string): string => {
    return sectionName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Dialog
      header={`Analyst Notes - ${moleculeName}`}
      visible={visible}
      onHide={onHide}
      style={{ width: '70vw', maxWidth: '1100px' }}
      breakpoints={{ '960px': '85vw', '641px': '95vw' }}
      className={styles.notesModal}
      closable={true}
      closeOnEscape={true}
    >
      <div className={styles.notesContainer}>
        {notes.length === 0 ? (
          <div className={styles.emptyState}>
            <i className="pi pi-info-circle" style={{ fontSize: '3rem', color: '#9ca3af' }}></i>
            <p>No notes available for this bid</p>
          </div>
        ) : (
          <Accordion
            activeIndex={activeIndex}
            onTabChange={e => setActiveIndex(e.index)}
            multiple
            className={styles.notesAccordion}
          >
            {sortedSections.map(sectionKey => (
              <AccordionTab
                key={sectionKey}
                header={
                  <div className={styles.accordionHeader}>
                    <i
                      className="pi pi-bookmark"
                      style={{ marginRight: '0.5rem', fontSize: '1rem' }}
                    ></i>
                    <span className={styles.sectionTitle}>{formatSectionName(sectionKey)}</span>
                    <span className={styles.notesCount}>
                      ({groupedNotes[sectionKey].length} note
                      {groupedNotes[sectionKey].length !== 1 ? 's' : ''})
                    </span>
                  </div>
                }
              >
                <div className={styles.sectionNotes}>
                  {groupedNotes[sectionKey].map(note => (
                    <div key={note.id} className={styles.noteItem}>
                      <div className={styles.noteHeader}>
                        <div className={styles.noteAuthor}>
                          <i
                            className="pi pi-user"
                            style={{ marginRight: '0.25rem', fontSize: '0.875rem' }}
                          ></i>
                          {note.created_by}
                        </div>
                        <div className={styles.noteTimestamp}>
                          {formatDate(note.is_edited ? note.updated_at : note.created_at)}
                          {note.is_edited && (
                            <span className={styles.editedBadge}>
                              <i
                                className="pi pi-pencil"
                                style={{ fontSize: '0.75rem', marginRight: '0.25rem' }}
                              ></i>
                              Edited
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.noteContent}>{note.content}</div>
                    </div>
                  ))}
                </div>
              </AccordionTab>
            ))}
          </Accordion>
        )}
      </div>
    </Dialog>
  );
};

export default NotesViewModal;
